import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import useDebounce from '../../hooks/debounceHook';

/**
 * @typedef {Object} ProjectFormData
 * @property {string} name - Project name
 * @property {string} description - Project description
 * @property {string} primary_epic - Primary epic ID
 * @property {string} start_date - Project start date (YYYY-MM-DD)
 * @property {string} end_date - Project end date (YYYY-MM-DD)
 * @property {number} [workspace] - Workspace ID (added on submit)
 */

/**
 * @typedef {Object} Epic
 * @property {number} id - Epic ID
 * @property {string} name - Epic name
 */

/**
 * @typedef {Object} CreateProjectDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {function} onClose - Function to call when dialog should close
 * @property {function} onSuccess - Function to call when project is created successfully
 */

/**
 * Dialog component for creating new projects
 * @component
 * @param {CreateProjectDialogProps} props - Component props
 * @returns {React.ReactElement} The create project dialog
 */
const CreateProjectDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    primary_epic: '',
    start_date: '',
    end_date: '',
  });
  const [epics, setEpics] = useState([]);
  const { currentWorkspace } = useWorkspace();

  /**
   * Debounced function to create a project
   * @param {ProjectFormData} projectData - The project data to submit
   */
  const debouncedCreateProject = useDebounce(async (projectData) => {
    try {
      await api.post('projects/', projectData);
      onSuccess();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  }, 1000);

  useEffect(() => {
    /**
     * Fetches available epics from the API
     * @async
     * @returns {Promise<void>}
     */
    const fetchEpics = async () => {
      try {
        const response = await api.get('epics/', {
          params: { workspace_id: currentWorkspace.id },
        });
        setEpics(response.data);
      } catch (error) {
        console.error('Error fetching epics:', error);
      }
    };

    fetchEpics();
  }, [currentWorkspace.id]);

  /**
   * Handles form submission
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   * @returns {void}
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    formData.workspace = currentWorkspace.id;
    debouncedCreateProject(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Project</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <Select
            margin="dense"
            label="Epic"
            fullWidth
            name="Epic"
            value={formData.primary_epic}
            onChange={(e) => setFormData({ ...formData, primary_epic: e.target.value })}
          >
            <MenuItem value="">None</MenuItem>
            {epics.map((primary_epic) => (
              <MenuItem key={primary_epic.id} value={primary_epic.id}>
                {primary_epic.name}
              </MenuItem>
            ))}
          </Select>
          <TextField
            margin="dense"
            label="Start Date"
            type="date"
            fullWidth
            required
            value={formData.start_date}
            onChange={(e) =>
              setFormData({ ...formData, start_date: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="End Date"
            type="date"
            fullWidth
            required
            value={formData.end_date}
            onChange={(e) =>
              setFormData({ ...formData, end_date: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateProjectDialog;
